import * as THREE from 'three';

import { digitAtPower, powersForValue, toSlotRotation } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import { deriveNaturalDivisionTrace } from '@/adapters/primitive/_pure/model/natural/division';
import { deriveNaturalLessThanTrace } from '@/adapters/primitive/_pure/model/natural/comparison';
import { projectNaturalLessThanBooleanChannel } from '@/adapters/primitive/_pure/derivation/natural/lessThanTraceProjection';
import { derivePrimitiveCameraFocus } from '@/adapters/primitive/_pure/derivation/cameraFocus';
import {
    buildFlightWheel,
    buildNaturalPlaceholderWheel,
    buildOperandWheel,
    buildResultWheel,
    toWheelLayout,
    toWheelPosition,
    type PrimitiveWheelLayout,
} from '@/adapters/primitive/_pure/derivation/wheels';
import { toNaturalReceiverLocationPosition } from '@/adapters/primitive/_pure/derivation/natural/locationProjection';
import { toShatterOffset, toShatterSpread } from '@/adapters/primitive/_pure/derivation/natural/shatter';
import { projectNaturalTraceToStepStates } from '@/adapters/primitive/_pure/derivation/natural/traceProjection';
import { naturalFlightDurationMs } from '@/adapters/primitive/_pure/derivation/natural/naturalFlightAnimation';
import { withDivisionComparisons } from '@/adapters/primitive/_pure/derivation/natural/divisionComparisons';
import { momentKey, momentPart, stepMoment, withMomentFrame, withMomentParameters } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '@/adapters/primitive/_contract/_~generated/operationDirectionEffect';
import type { NaturalEventEnvelope } from '@/adapters/primitive/_contract/natural/event';
import type {
    Config,
    PrimitiveWheelRole,
    PrimitiveWheelState,
} from '@/adapters/primitive/_contract/config';
import type { PrimitiveKeyframe, PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { NaturalDivisionTrace } from '@/adapters/primitive/_contract/trace';

type DivisionPhase = 'initialization' | 'subtraction' | 'quotient-increment';

function remapRole(role: PrimitiveWheelRole, phase: DivisionPhase): PrimitiveWheelRole {
    if (phase === 'initialization') {
        switch (role) {
            case 'addend-one': return 'dividend';
            case 'addend-two': return 'divisor';
            case 'sum': return 'remainder';
            default: return role;
        }
    }
    if (phase === 'subtraction') {
        switch (role) {
            case 'minuend': return 'remainder';
            case 'subtrahend': return 'divisor';
            case 'difference': return 'remainder';
            default: return role;
        }
    }
    switch (role) {
        case 'addend-one':
        case 'addend-two':
        case 'sum':
            return 'quotient';
        default:
            return role;
    }
}

function remapEnvelope(envelope: NaturalEventEnvelope, phase: DivisionPhase): NaturalEventEnvelope {
    const event = envelope.event;
    const mapped = {
        ...event,
        ...('contributionTraveller' in event
            ? { contributionTraveller: { ...event.contributionTraveller, wheelRole: remapRole(event.contributionTraveller.wheelRole, phase) } }
            : {}),
        ...('operandWheel' in event
            ? { operandWheel: { ...event.operandWheel, wheelRole: remapRole(event.operandWheel.wheelRole, phase) } }
            : {}),
        ...('location' in event
            ? { location: { ...event.location, receiverRole: remapRole(event.location.receiverRole, phase) } }
            : {}),
        ...('receiverInteraction' in event
            ? {
                receiverInteraction: {
                    ...event.receiverInteraction,
                    receiver: {
                        ...event.receiverInteraction.receiver,
                        wheelRole: remapRole(event.receiverInteraction.receiver.wheelRole, phase),
                    },
                },
            }
            : {}),
            ...('receiverRole' in event
                ? { receiverRole: remapRole(event.receiverRole, phase) }
                : {}),
    } as NaturalEventEnvelope['event'];
    return { ...envelope, event: mapped };
}

function valueWheel(args: {
    layout: PrimitiveWheelLayout;
    role: 'remainder' | 'quotient';
    value: number;
}): PrimitiveWheelState {
    const powers = powersForValue(args.value);
    return buildResultWheel({
        layout: args.layout,
        role: args.role,
        powers,
        countedThroughDigitByPower: Object.fromEntries(
            powers.map((power) => [power, digitAtPower({ value: args.value, power })]),
        ),
    });
}

function buildFixedWheels(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    dividend: number;
    divisor: number;
    remainder: number;
    quotient: number;
    divisorSpent?: boolean;
    quotientPlaceholder?: boolean;
}): readonly PrimitiveWheelState[] {
    const { config, layout } = args;
    return [
        buildOperandWheel({
            layout,
            role: 'dividend',
            value: args.dividend,
            bandOpacityByPower: Object.fromEntries(
                powersForValue(args.dividend)
                    .map((power) => [power, config.natural.display.band.spentOpacity]),
            ),
        }),
        buildOperandWheel({
            layout,
            role: 'divisor',
            value: args.divisor,
            ...(args.divisorSpent
                ? {
                    bandOpacityByPower: Object.fromEntries(
                        powersForValue(args.divisor)
                            .map((power) => [power, config.natural.display.band.spentOpacity]),
                    ),
                }
                : {}),
        }),
        valueWheel({ layout, role: 'remainder', value: args.remainder }),
        args.quotientPlaceholder
            ? buildNaturalPlaceholderWheel({ layout, role: 'quotient' })
            : valueWheel({ layout, role: 'quotient', value: args.quotient }),
    ];
}

function remapWheel(args: {
    layout: PrimitiveWheelLayout;
    divisor: number;
    wheel: PrimitiveWheelState;
    phase: DivisionPhase;
    depthOffset: number;
    materializedResultValue?: number;
}): PrimitiveWheelState | null {
    const { wheel, phase, depthOffset } = args;
    if (phase === 'initialization' && wheel.variant === undefined && wheel.role === 'addend-two') {
        // The initialization column stands in for zero, so its dimming only covers 10^0.
        const spentOpacity = Object.values(wheel.bandOpacityByPower ?? {})[0];
        const divisor = buildOperandWheel({
            layout: args.layout,
            role: 'divisor',
            value: args.divisor,
            ...(spentOpacity === undefined
                ? {}
                : {
                    bandOpacityByPower: Object.fromEntries(
                        powersForValue(args.divisor).map((power) => [power, spentOpacity]),
                    ),
                }),
        });
        return {
            ...divisor,
            position: [
                divisor.position[0],
                divisor.position[1],
                wheel.position[2] + depthOffset,
            ],
        };
    }
    if (wheel.variant === undefined && (
        phase === 'subtraction' && wheel.role === 'minuend'
        || phase === 'quotient-increment' && (wheel.role === 'addend-one' || wheel.role === 'addend-two')
    )) return null;
    if (wheel.variant === undefined
        && wheel.valueKind === 'placeholder'
        && args.materializedResultValue !== undefined) {
        return valueWheel({
            layout: args.layout,
            role: phase === 'subtraction' ? 'remainder' : 'quotient',
            value: args.materializedResultValue,
        });
    }

    return {
        ...wheel,
        role: remapRole(wheel.role, phase),
        ...(phase === 'quotient-increment' && wheel.helperWheel?.kind === 'Contribution'
            ? { helperWheel: { key: 'unit:quotient', kind: 'Unit' as const } } : {}),
        position: [wheel.position[0], wheel.position[1], wheel.position[2] + depthOffset],
        ...(wheel.positionByPower === undefined
            ? {}
            : {
                positionByPower: Object.fromEntries(Object.entries(wheel.positionByPower).flatMap(
                    ([power, position]) => position === undefined
                        ? []
                        : [[power, [position[0], position[1], position[2] + depthOffset]]],
                )),
            }),
        ...(wheel.receiverLocation === undefined
            ? {}
            : {
                receiverLocation: {
                    ...wheel.receiverLocation,
                    receiverRole: remapRole(wheel.receiverLocation.receiverRole, phase),
                },
            }),
    };
}

function remapStep(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    dividend: number;
    divisor: number;
    step: PrimitiveStepState;
    phase: DivisionPhase;
    cycleIndex: number;
    remainder: number;
    quotient: number;
    divisorSpent?: boolean;
    quotientPlaceholder?: boolean;
    materializedResultValue?: number;
}): PrimitiveStepState {
    const { config, layout, phase } = args;
    const sourceAnchor = phase === 'initialization'
        ? 'addend-one'
        : phase === 'subtraction'
            ? 'subtrahend'
            : 'sum';
    const targetAnchor = phase === 'initialization'
        ? 'dividend'
        : phase === 'subtraction'
            ? 'divisor'
            : 'quotient';
    const depthOffset = toWheelPosition({ layout, role: targetAnchor })[2]
        - toWheelPosition({ layout, role: sourceAnchor })[2];
    const staticWheels = buildFixedWheels({
        config,
        layout,
        dividend: args.dividend,
        divisor: args.divisor,
        remainder: args.remainder,
        quotient: args.quotient,
        divisorSpent: args.divisorSpent,
        quotientPlaceholder: args.quotientPlaceholder,
    });
    const mappedKeyframes: PrimitiveKeyframe[] = args.step.keyframes.map((keyframe) => {
        const projected = keyframe.wheels
            .map((wheel) => remapWheel({
                layout,
                divisor: args.divisor,
                wheel,
                phase,
                depthOffset,
                materializedResultValue: args.materializedResultValue,
            }))
            .filter((wheel): wheel is PrimitiveWheelState => wheel !== null);
        const projectedRoles = new Set(projected.filter((wheel) => wheel.variant === undefined).map((wheel) => wheel.role));
        return {
            ...keyframe,
            wheels: [
                ...staticWheels.filter((wheel) => !projectedRoles.has(wheel.role)),
                ...projected,
            ],
            events: keyframe.events.map((event) => remapEnvelope(event, phase)),
        };
    });
    const replaceTerms = (text: string) => phase === 'initialization'
        ? text.replaceAll('AddendOne', 'Dividend').replaceAll('AddendTwo', 'Divisor').replaceAll('Sum', 'Remainder')
        : phase === 'subtraction'
            ? text.replaceAll('Subtrahend', 'Divisor').replaceAll('Difference', 'Remainder')
            : text.replaceAll('AddendTwo', 'Unit').replaceAll('Sum', 'Quotient');
    return {
        ...withMomentFrame(args.step, phase === 'initialization'
            ? momentPart('initialization')
            : momentPart(phase, undefined, args.cycleIndex)),
        label: replaceTerms(args.step.label),
        description: replaceTerms(args.step.description),
        keyframes: mappedKeyframes,
    };
}

export function deriveNaturalDivisionStepStates(args: {
    config: Config;
    dividend: number;
    divisor: number;
    divisionComparison?: 'explicit' | 'integrated';
    splitRemainderSettlement?: boolean;
    options?: import('@/adapters/primitive/_contract/natural/arithmetic').PrimitiveOperationDerivationOptions;
}): readonly PrimitiveStepState[] {
    return projectNaturalDivisionTrace({ ...args, trace: deriveNaturalDivisionTrace({
        dividend: args.dividend, divisor: args.divisor, radix: args.config.natural.radix,
    }, { ...args.options, runawayDivisionCycleBudget: args.config.natural.runawayBudget.divisionCycles }) });
}

export function projectNaturalDivisionTrace(args: {
    config: Config;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    dividend: number;
    divisor: number;
    trace: NaturalDivisionTrace;
    divisionComparison?: 'explicit' | 'integrated';
    splitRemainderSettlement?: boolean;
    /** A sampled continuation already has a resident Quotient before its first shown cycle. */
    residentQuotient?: boolean;
    fullComparisons?: number;
}): readonly PrimitiveStepState[] {
    const { dividend, divisor } = args;
    const config = args.config;
    const layout = toWheelLayout({ config, operandSchedulingMode: 'grouped' });
    const fixedWheels = (options: Omit<Parameters<typeof buildFixedWheels>[0], 'dividend' | 'divisor'>) => (
        buildFixedWheels({ ...options, dividend, divisor })
    );
    const division = args.trace;
    const steps = projectNaturalTraceToStepStates({
        config,
        operandSchedulingMode: 'grouped',
        operation: 'addition',
        operandOne: division.dividend,
        operandTwo: 0,
        trace: division.initializationTrace,
        dimTerminalOperands: false,
    }).steps
        // Initialization is `Dividend + 0`; that zero column is bookkeeping, not the Divisor.
        .filter((step) => step.moment?.step.parameters?.role !== 'second')
        .map((step) => remapStep({
        config,
        layout,
        dividend,
        divisor,
        step,
        phase: 'initialization',
        cycleIndex: 0,
        remainder: 0,
        quotient: 0,
        quotientPlaceholder: true,
    }));

    if (division.cycles.length === 0) {
        const completion = steps.at(-1);
        if (completion === undefined) {
            throw new Error('Natural division initialization is missing completion.');
        }
        steps[steps.length - 1] = {
            ...completion,
            keyframes: [
                ...completion.keyframes,
                {
                    wheels: fixedWheels({
                        config,
                        layout,
                        remainder: division.dividend,
                        quotient: 0,
                        divisorSpent: args.divisionComparison !== 'explicit',
                    }),
                    transition: { durationMs: config.base.animation?.fadeDurationMs ?? 0 },
                    events: [],
                },
            ],
        };
    }

    for (const cycle of division.cycles) {
        const subtractionSteps = projectNaturalTraceToStepStates({
            config,
            operandSchedulingMode: 'grouped',
            operation: 'subtraction',
            operandOne: cycle.remainderBefore,
            operandTwo: division.divisor,
            trace: cycle.subtractionTrace,
            dimTerminalOperands: false,
        }).steps;
        const projectedSubtractionSteps = subtractionSteps.slice(1).map((step) => remapStep({
            config,
            layout,
            dividend,
            divisor,
            step,
            phase: 'subtraction',
            cycleIndex: cycle.cycleIndex,
            remainder: cycle.remainderAfter,
            quotient: cycle.quotientBefore,
            quotientPlaceholder: cycle.cycleIndex === 1 && !args.residentQuotient,
            materializedResultValue: cycle.remainderBefore,
        }));

        const quotientSteps = projectNaturalTraceToStepStates({
            config,
            operandSchedulingMode: 'grouped',
            operation: 'addition',
            operandOne: cycle.quotientBefore,
            operandTwo: 1,
            trace: cycle.quotientIncrementTrace,
            dimTerminalOperands: false,
        }).steps;
        const projectedQuotientSteps = quotientSteps.slice(1).map((step) => remapStep({
            config,
            layout,
            dividend,
            divisor,
            step,
            phase: 'quotient-increment',
            cycleIndex: cycle.cycleIndex,
            remainder: cycle.remainderAfter,
            quotient: cycle.quotientAfter,
            divisorSpent: true,
            ...(cycle.cycleIndex === 1 && !args.residentQuotient
                ? {}
                : { materializedResultValue: cycle.quotientBefore }),
        }));
        const introduction = cycle.quotientIncrementTrace.points.find(
            (point) => point.event.event.eventKind === 'contribution-traveller-taken-off',
        );
        if (introduction === undefined) {
            throw new Error('Natural division Quotient increment is missing Unit introduction.');
        }
        const quotientContributionTraveller = introduction.nextState.contributionTraveller;
        if (quotientContributionTraveller === null) {
            throw new Error('Natural division Quotient increment is missing its Contribution-traveller.');
        }

        if (projectedSubtractionSteps.at(-1)?.label === 'Complete') {
            const completion = projectedSubtractionSteps.pop()!;
            const settlement = projectedSubtractionSteps.at(-1);
            if (settlement === undefined) throw new Error('Natural division subtraction is missing settlement.');
            projectedSubtractionSteps[projectedSubtractionSteps.length - 1] = {
                ...settlement,
                keyframes: [...settlement.keyframes, ...completion.keyframes],
            };
        }

        const settlement = projectedSubtractionSteps.at(-1);
        const firstQuotientStep = projectedQuotientSteps[0];
        if (settlement === undefined || firstQuotientStep === undefined) {
            throw new Error('Natural division cycle is missing projected steps.');
        }
        const nearPosition = toNaturalReceiverLocationPosition({
            config,
            layout,
            location: { receiverRole: 'quotient', locationKind: 'standoff-near' },
        });
        const unitWheel = (opacity: number, entranceGrowth: number) => ({ ...buildFlightWheel({
            role: 'quotient',
            value: 1,
            power: quotientContributionTraveller.band.power,
            position: nearPosition,
            rotation: toSlotRotation({ slotOffset: quotientContributionTraveller.slotOffset, radix: config.natural.radix }),
            opacity,
            countedDigits: quotientContributionTraveller.band.countedSlots,
            overhangDigits: quotientContributionTraveller.band.overhangSlots,
            receiverLocation: { receiverRole: 'quotient', locationKind: 'standoff-near' },
        }), entranceGrowth, helperWheel: { key: 'unit:quotient', kind: 'Unit' as const } });
        const settledWheels = settlement.keyframes.at(-1)?.wheels ?? [];
        const hintHoldMs = config.base.animation?.assemblyHintHoldDurationMs ?? 0;
        if (args.splitRemainderSettlement) {
            projectedSubtractionSteps[projectedSubtractionSteps.length - 1] = {
                ...withMomentParameters(settlement, { settlement: 'remainder' }),
            };
        }
        const withUnit: PrimitiveStepState = {
            ...settlement,
            keyframes: [
                ...(args.splitRemainderSettlement ? [] : settlement.keyframes),
                {
                    wheels: [...settledWheels, unitWheel(0, 0)],
                    transition: 'instant',
                    events: [remapEnvelope(introduction.event, 'quotient-increment')],
                },
                // No camera move precedes the Unit, so its hint ring is held before the Unit grows out of it.
                ...(hintHoldMs > 0 ? [{
                    wheels: [...settledWheels, unitWheel(0, 0)],
                    transition: { durationMs: hintHoldMs },
                    events: [],
                }] : []),
                {
                    wheels: [...settledWheels, unitWheel(1, 1)],
                    transition: { durationMs: config.base.animation?.fadeDurationMs ?? 0 },
                    events: [],
                },
            ],
        };
        if (args.splitRemainderSettlement) projectedSubtractionSteps.push(withUnit);
        else projectedSubtractionSteps[projectedSubtractionSteps.length - 1] = withUnit;

        const remainingFirstKeyframes = firstQuotientStep.keyframes.filter((keyframe) => !keyframe.events.some(
            (event) => event.event.eventKind === 'contribution-traveller-taken-off',
        ));
        const firstTarget = remainingFirstKeyframes[0]?.wheels.find((wheel) => wheel.variant === 'flight')?.position;
        if (firstTarget !== undefined && remainingFirstKeyframes[0].transition !== 'instant') {
            remainingFirstKeyframes[0] = {
                ...remainingFirstKeyframes[0],
                transition: {
                    durationMs: naturalFlightDurationMs({ config, from: nearPosition, to: firstTarget }),
                },
            };
        }
        projectedQuotientSteps[0] = { ...firstQuotientStep, keyframes: remainingFirstKeyframes };

        const finalQuotientStep = projectedQuotientSteps.at(-1);
        if (finalQuotientStep === undefined) {
            throw new Error('Natural division cycle is missing final Quotient settlement.');
        }
        const terminalCycle = cycle.cycleIndex === division.cycles.length;
        projectedQuotientSteps[projectedQuotientSteps.length - 1] = {
            ...finalQuotientStep,
            keyframes: [
                ...finalQuotientStep.keyframes,
                {
                    wheels: fixedWheels({
                        config,
                        layout,
                        remainder: cycle.remainderAfter,
                        quotient: cycle.quotientAfter,
                        divisorSpent: args.divisionComparison === 'integrated'
                            || terminalCycle && args.divisionComparison !== 'explicit',
                    }),
                    transition: terminalCycle || args.divisionComparison === 'integrated'
                        ? 'instant'
                        : { durationMs: config.base.animation?.fadeDurationMs ?? 0 },
                    events: [],
                },
            ],
        };

        steps.push(...projectedSubtractionSteps, ...projectedQuotientSteps);
    }

    if (division.outcome.outcomeKind === 'failed') {
        const spread = toShatterSpread(config);
        const restingWheels = fixedWheels({
            config,
            layout,
            remainder: division.remainder,
            quotient: division.quotient,
            divisorSpent: true,
        });
        const shatteredWheels = restingWheels.map((wheel) => (wheel.role === 'quotient'
            ? {
                ...wheel,
                positionByPower: Object.fromEntries(wheel.powers.map((power, memberIndex) => {
                    const offset = toShatterOffset({ memberIndex, scatter: 1, spread });
                    return [power, [
                        wheel.position[0] + offset[0],
                        wheel.position[1] + offset[1],
                        wheel.position[2] + offset[2],
                    ] as [number, number, number]];
                })),
                wheelOpacity: 0,
            }
            : wheel));
        steps.push({
            ...momentKey(stepMoment('shatter-quotient')),
            label: 'Shatter Quotient',
            description: `The Remainder never moves, so the Quotient gains a ring at every decade and `
                + `never becomes a Natural number; the stream is shown for ${division.outcome.cutoff?.limit ?? 0} cycles.`,
            keyframes: [
                { wheels: restingWheels, transition: 'instant', events: [] },
                {
                    wheels: shatteredWheels,
                    transition: { durationMs: config.base.animation?.fadeDurationMs ?? 0 },
                    events: [],
                },
            ],
        });
    }

    return args.divisionComparison === 'integrated'
        ? withDivisionComparisons({ config, trace: division, steps, directionCatalog: args.directionCatalog,
            fullComparisons: args.fullComparisons })
        : args.divisionComparison === 'explicit'
        ? withExplicitComparisons({ config, layout, division, steps, directionCatalog: args.directionCatalog })
        : steps;
}

function withExplicitComparisons(args: {
    config: Config;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    layout: PrimitiveWheelLayout;
    division: NaturalDivisionTrace;
    steps: readonly PrimitiveStepState[];
}): readonly PrimitiveStepState[] {
    const { config, layout, division } = args;
    const divisorPosition = toWheelPosition({ layout, role: 'divisor' });
    const remainderPosition = toWheelPosition({ layout, role: 'remainder' });
    const midpoint = (divisorPosition[2] + remainderPosition[2]) / 2;
    const halfSeparation = (divisorPosition[2] - remainderPosition[2]) / 2;
    const radius = config.base.display.wheel.innerRadius
        + powersForValue(Math.max(division.dividend, division.divisor)).at(-1)!
            * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap)
        + config.base.display.wheel.ringWidth;
    const depth = Math.max(config.boolean.display.layout.resultChannelDepth,
        layout.wheelGap * 2, radius * 2 + layout.wheelGap);
    const positions = {
        inputOne: [-halfSeparation, 0, 0] as [number, number, number],
        inputTwo: [halfSeparation, 0, 0] as [number, number, number],
        output: [0, 0, -depth] as [number, number, number],
    };
    const turn = new THREE.Matrix4().makeRotationY(-Math.PI / 2);
    // Local operand separation becomes axial separation; the Result sits sideways off the division rod.
    const point = ([x, y, z]: readonly number[]): [number, number, number] => [z === 0 ? 0 : -z, y, x + midpoint];
    const direction = ([x, y, z]: readonly number[]): [number, number, number] => [z === 0 ? 0 : -z, y, x];
    const unwrap = (angle: number, previous: number) => angle + Math.round((previous - angle) / (2 * Math.PI)) * 2 * Math.PI;
    const rotate = (rotation: [number, number, number], previous?: [number, number, number]): [number, number, number] => {
        const matrix = turn.clone().multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(...rotation)));
        const euler = new THREE.Euler().setFromRotationMatrix(matrix);
        return previous
            ? [unwrap(euler.x, previous[0]), unwrap(euler.y, previous[1]), unwrap(euler.z, previous[2])]
            : [euler.x, euler.y, euler.z];
    };
    const project = (remainder: number): PrimitiveStepState[] => {
        const rotations = new Map<PrimitiveWheelRole, [number, number, number]>();
        return projectNaturalLessThanBooleanChannel({
                config, source: remainder, target: division.divisor, positions, directionCatalog: args.directionCatalog,
                trace: deriveNaturalLessThanTrace({ source: remainder, target: division.divisor, radix: config.natural.radix }),
        }).map(step => ({
                ...step,
                keyframes: step.keyframes.map(frame => ({
                    ...frame,
                    wheels: frame.wheels.map(wheel => {
                        const rotation = rotate(wheel.wheelRotation ?? [Math.PI / 2, 0, 0], rotations.get(wheel.role));
                        rotations.set(wheel.role, rotation);
                        return {
                            ...wheel,
                            role: wheel.role === 'less-than-source' ? 'remainder' as const
                                : wheel.role === 'less-than-target' ? 'divisor' as const : wheel.role,
                            position: point(wheel.position),
                            wheelRotation: rotation,
                        };
                    }),
                    booleanRod: frame.booleanRod && {
                        segments: frame.booleanRod.segments.map(segment => ({
                            ...segment, from: point(segment.from), to: point(segment.to),
                            ...(segment.profile ? { profile: { ...segment.profile, lateral: direction(segment.profile.lateral) } } : {}),
                        })),
                    },
                })),
        }));
    };
    const initial = project(division.dividend)[0].keyframes[0];
    const channel = { segments: initial.booleanRod!.segments.filter(segment =>
        !segment.segmentKey.includes('input-') && !segment.segmentKey.startsWith('traveller:')) };
    let decision: PrimitiveWheelState = initial.wheels.find(wheel => wheel.role === 'less-than-decision')!;
    const decorated = (frame: PrimitiveKeyframe): PrimitiveKeyframe => ({
        ...frame, wheels: [...frame.wheels, decision], booleanRod: channel,
    });
    const result: PrimitiveStepState[] = [];
    const compare = (index: number, remainder: number, quotient: number, quotientPlaceholder: boolean) => {
        const resting = buildFixedWheels({
            config, layout, dividend: division.dividend, divisor: division.divisor,
            remainder, quotient, quotientPlaceholder,
        }).map(wheel => wheel.role === 'divisor' || wheel.role === 'remainder'
            ? { ...wheel, wheelRotation: [Math.PI / 2, 0, 0] as [number, number, number] } : wheel);
        const comparison = project(remainder).filter(step => step.stepKey !== 'complete');
        decision = initial.wheels.find(wheel => wheel.role === 'less-than-decision')!;
        // Introduce hidden leading-zero bands before a timed turn, and remove them only after returning.
        const paddedResting = resting.map(wheel => {
            const operand = comparison[0].keyframes[0].wheels.find(candidate => candidate.role === wheel.role);
            if (!operand || (wheel.role !== 'divisor' && wheel.role !== 'remainder')) return wheel;
            return {
                ...wheel, powers: operand.powers,
                bandOpacityByPower: Object.fromEntries(operand.powers.map(power =>
                    [power, wheel.powers.includes(power) ? 1 : 0])),
            };
        });
        const restingFrame = (): PrimitiveKeyframe => decorated({ wheels: paddedResting, transition: 'instant', events: [] });
        const frames: PrimitiveStepState[] = comparison.map(step => ({
            ...withMomentFrame(step, momentPart('comparison', undefined, index)),
            cameraFocus: { stationKey: 'division-comparison', viewKey: 'division-comparison',
                roles: ['remainder', 'divisor', 'less-than-decision'] },
            keyframes: step.keyframes.map(frame => ({
                ...frame, wheels: [
                    ...resting.filter(wheel => wheel.role !== 'divisor' && wheel.role !== 'remainder'),
                    ...frame.wheels,
                ],
            })),
        }));
        const entry = frames[0];
        const durationMs = config.natural.animation?.naturalLessThan?.helperEntryDurationMs ?? 0;
        entry.keyframes = [restingFrame(), { ...entry.keyframes[0], transition: { durationMs } }];
        result.push(...frames);
        const last = frames.at(-1)!.keyframes.at(-1)!;
        decision = last.wheels.find(wheel => wheel.role === 'less-than-decision')!;
        const returned = restingFrame();
        returned.wheels = returned.wheels.map(wheel => {
            if (wheel.role === 'divisor' && remainder < division.divisor) {
                wheel = { ...wheel, bandOpacityByPower: Object.fromEntries(wheel.powers.map(power =>
                    [power, config.natural.display.band.spentOpacity])) };
            }
            const previous = last.wheels.find(candidate => candidate.role === wheel.role)?.wheelRotation;
            const rotation = wheel.wheelRotation;
            return previous && rotation ? {
                ...wheel, wheelRotation: [
                    unwrap(rotation[0], previous[0]), unwrap(rotation[1], previous[1]), unwrap(rotation[2], previous[2]),
                ] as [number, number, number],
            } : wheel;
        });
        result.push({
            ...momentKey({ frames: [momentPart('comparison', undefined, index)], step: momentPart('return') }),
            cameraFocus: entry.cameraFocus,
            label: remainder < division.divisor ? 'Finish Division' : 'Continue Division',
            description: `${remainder} < ${division.divisor} is ${remainder < division.divisor}; `
                + (remainder < division.divisor ? 'no whole Divisor remains.' : 'restore the operands for another subtraction.'),
            keyframes: [
                { ...last, transition: 'instant' },
                { ...last, booleanRod: { segments: last.booleanRod!.segments.map(segment => ({
                    ...segment, opacity: segment.segmentKey.includes('input-') || segment.segmentKey.startsWith('traveller:')
                        ? 0 : segment.opacity,
                })) }, transition: { durationMs: config.natural.animation?.fadeDurationMs ?? 0 } },
                { ...returned, transition: { durationMs } },
                { ...returned, transition: 'instant', wheels: returned.wheels.map(wheel => {
                    const original = resting.find(candidate => candidate.role === wheel.role);
                    return original ? { ...wheel, powers: original.powers } : wheel;
                }) },
            ],
        });
    };
    let previous: readonly PrimitiveWheelState[] = [];
    for (const step of args.steps) {
        const frame = step.moment?.frames[0];
        const cycle = frame?.kind === 'subtraction'
            ? division.cycles.find(cycle => frame.ordinal?.index === cycle.cycleIndex - 1) : undefined;
        if (cycle && !result.some(entry => entry.moment?.frames[0]?.kind === 'comparison'
            && entry.moment.frames[0].ordinal?.index === cycle.cycleIndex - 1 && entry.moment.step.kind === 'initial')) {
            compare(cycle.cycleIndex, cycle.remainderBefore, cycle.quotientBefore, cycle.cycleIndex === 1);
        }
        result.push({ ...step, cameraFocus: derivePrimitiveCameraFocus(step, previous),
            keyframes: step.keyframes.map(decorated) });
        previous = step.keyframes.at(-1)!.wheels;
    }
    if (division.outcome.outcomeKind !== 'failed') {
        compare(division.cycles.length + 1, division.remainder, division.quotient, false);
    }
    return result;
}